import { DownloadOutlined } from '@ant-design/icons';
import { useQuery } from '@tanstack/react-query';
import { Alert, ConfigProvider, DatePicker, Input, Spin } from 'antd';
import { ColumnsType } from 'antd/es/table';
import { Dayjs } from 'dayjs';
import { debounce } from 'lodash';
import moment from 'moment';
import React, { FC, useCallback, useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { setAccessControlEntries } from '../../contextRedux/AccessControlEntry/AccessControlEntryStore';
import { AnimateButton } from '../../customComponents/button/CustomButton';
import { CustomTable } from '../../customComponents/table/CustomTable';
import AccessControlEntryService from '../../services/accessControlEntry/AccessControlEntryService';
import { saveBlobCrossPlatform } from '../../utils/saveBlobCrossPlatform';

// ✅ Define Interface for Data
interface AccessControlEventReport {
  key: string; // Required for Ant Design Table
  eventReference: string;
  date: string;
  gateReference: string;
  faceReference: string;
  cardReference: string;
  cameras: string;
  isMatch: boolean;
}

const AccessControlEntry: FC = () => {
  const dispatch = useDispatch();

  const [dataSource, setDataSource] = useState<AccessControlEventReport[]>([]);
  const [searchText, setSearchText] = useState<string>('');
  const [selectedMonth, setSelectedMonth] = useState<Dayjs | null>(null);

  // 🔹 Format date for API request
  const date = selectedMonth ? selectedMonth.format('YYYY-MM') + '-01' : null;

  // 🔹 Fetch Data using React Query
  const {
    data: events,
    isFetching,
    isError,
    error,
  } = useQuery({
    queryKey: ['accessControlEntries', searchText, date],
    queryFn: () =>
      AccessControlEntryService.report({ termen: searchText, date }).then((res: any) => {
        const formattedData =
          res?.response?.flatMap((item: any) =>
            (item.entries || []).map((entry: any) => ({
              key: `${item.eventReference}-${entry.date}-${entry.minTime}`,
              eventReference: item.eventReference,
              date: `${entry.date} ${entry.minTime}`,
              gateReference: item.gateReference,
              faceReference: item.faceReference,
              cardReference: item.cardReference,
              cameras: (item.devices || [])
                .map(
                  (dev: any) =>
                    `${dev.cameraReference} - Nr. Necunoscute: ${dev.personUnknowns} - Cunoscute: ${dev.personKnowns}`
                )
                .join(' | '),
              isMatch: item.isMatch,
            }))
          ) ?? [];

        dispatch(setAccessControlEntries(formattedData));
        return formattedData;
      }),
  });

  // 🔹 Define Table Columns
  const columns: ColumnsType<AccessControlEventReport> = [
    {
      title: 'Event Reference',
      dataIndex: 'eventReference',
      key: 'eventReference',
    },
    {
      title: 'Date',
      dataIndex: 'date',
      key: 'date',
      render: (value: string) => moment(value).format('YYYY-MM-DD HH:mm:ss'),
    },
    {
      title: 'Gate Reference',
      dataIndex: 'gateReference',
      key: 'gateReference',
    },
    {
      title: 'Face Reference',
      dataIndex: 'faceReference',
      key: 'faceReference',
    },
    {
      title: 'Card Reference',
      dataIndex: 'cardReference',
      key: 'cardReference',
    },
    {
      title: 'Cameras',
      dataIndex: 'cameras',
      key: 'cameras',
    },
  ];

  // 🔹 Update State when Data Changes
  useEffect(() => {
    if (events) {
      setDataSource(events);
    }
  }, [events]);

  // 🔹 Handle Search Input with Debounce
  const debouncedSetSearchText = useCallback(
    debounce((value: string) => {
      setSearchText(value);
    }, 500),
    []
  );

  const handleSearch = (e: React.ChangeEvent<HTMLInputElement>) => {
    debouncedSetSearchText(e.target.value);
  };

  const handleMonthChange = (date: Dayjs | null) => {
    setSelectedMonth(date);
  };

  // 🔹 Download Excel File
  const downloadExcel = () => {
    AccessControlEntryService.print({ date })
      .then((response: any) => {
        if (!response.ok) throw new Error('Network response was not ok');

        return response.blob().then((blob: Blob) => {
          saveBlobCrossPlatform(blob, 'AccessControlReport.xlsx');
        });
      })
      .catch((error) => console.error('Error downloading Excel:', error));
  };

  // 🔹 Loading & Error Handling
  if (isFetching) return <Spin size='large' className='spinner' />;
  if (isError) return <Alert message={error?.message} type='error' />;

  return (
    <>
      <h1 style={{ marginBottom: 40 }}>Access Control Report</h1>

      {/* 🔹 Controls: Search & Date Picker */}
      <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 16 }}>
        <div style={{ width: '30%', display: 'flex', flexDirection: 'column' }}>
          <Input.Search placeholder='Search...' allowClear onChange={handleSearch} style={{ marginBottom: 10 }} />
          <ConfigProvider theme={{ components: { DatePicker: { cellHoverBg: '#1677ff' } } }}>
            <DatePicker picker='month' value={selectedMonth} onChange={handleMonthChange} style={{ width: '100%' }} />
          </ConfigProvider>
        </div>

        <AnimateButton onClick={downloadExcel}>
          <DownloadOutlined />
          Excel
        </AnimateButton>
      </div>

      <CustomTable<AccessControlEventReport>
        initialDataSource={dataSource} // ✅ Correct prop name
        columns={columns}
        rowKey='key'
        pagination={false}
        className='table'
      />
    </>
  );
};

export default AccessControlEntry;
